/**
 * web/src/api/workflows.ts
 * Typed clients for /api/workflows/* (API-01). Server contract: src/pixel/api/workflows.py.
 *
 * Routes:
 *   GET    /api/workflows                        — listWorkflows(query)
 *   GET    /api/workflows/:id                    — fetchWorkflow(id, cursor?, limit?)
 *   POST   /api/workflows/trigger        (admin) — triggerWorkflow(body)
 *   POST   /api/workflows/:id/cancel     (admin) — cancelWorkflow(id)
 *   POST   /api/workflows/:id/retry/:idx (admin) — retryStep(id, stepIndex)
 *
 * All query strings are constructed via URLSearchParams (T-05-02-01) so
 * user-supplied filter values are URL-encoded by the platform.
 */
import { api } from '../lib/api';
import type {
  Workflow,
  WorkflowStatus,
  StepExecution,
  AuditEntry,
  TrackedEmail,
  OpsEmailThread,
} from './types';

/** Optional filters for GET /api/workflows. */
export interface ListWorkflowsQuery {
  eventId?: string;
  type?: string;
  status?: WorkflowStatus;
  /** Include archived (soft-hidden) workflows. Server defaults to false. */
  showArchived?: boolean;
}

/**
 * Lists workflows filtered by event/type/status. Server caps at 200 rows
 * ordered by createdAt desc.
 *
 * @param q optional filters
 * @returns Workflow[] (each row includes embedded `event`)
 * @throws ApiClientError on non-2xx
 * @sideEffects HTTP GET
 * @idempotent yes
 */
export async function listWorkflows(q: ListWorkflowsQuery = {}): Promise<Workflow[]> {
  const params = new URLSearchParams();
  if (q.eventId) params.set('eventId', q.eventId);
  if (q.type) params.set('type', q.type);
  if (q.status) params.set('status', q.status);
  if (q.showArchived) params.set('showArchived', 'true');
  const qs = params.toString();
  return api<Workflow[]>(`/api/workflows${qs ? `?${qs}` : ''}`);
}

/** GET /api/workflows/:id — workflow + steps + paginated audit + tracked emails. */
export interface WorkflowDetail {
  workflow: Workflow;
  steps: StepExecution[];
  auditLog: { entries: AuditEntry[]; nextCursor: string | null };
  trackedEmails: TrackedEmail[];
}

/**
 * Fetches a workflow's full detail bundle. Audit log uses cursor pagination
 * — pass `cursor` from a prior response's `auditLog.nextCursor` to load the
 * next page; `limit` defaults to 50 (server max 200).
 *
 * @param id workflow id
 * @param cursor opaque cursor from a prior response
 * @param limit page size (1-200, default 50)
 * @returns WorkflowDetail
 * @throws ApiClientError on non-2xx (404 → WORKFLOW_NOT_FOUND)
 * @sideEffects HTTP GET
 * @idempotent yes
 */
export async function fetchWorkflow(
  id: string,
  cursor?: string,
  limit = 50,
): Promise<WorkflowDetail> {
  const params = new URLSearchParams();
  if (cursor) params.set('cursor', cursor);
  params.set('limit', String(limit));
  return api<WorkflowDetail>(`/api/workflows/${id}?${params.toString()}`);
}

/** One AI call on a workflow (GET .../agent-runs). Metadata only — raw
 * input/output snapshots never leave the server. costUsd is admin-only. */
export interface AgentRunOut {
  id: string;
  agentName: string;
  model: string;
  status: string;
  stepExecutionId: string | null;
  turnCount: number;
  tokensInput: number | null;
  tokensOutput: number | null;
  durationMs: number | null;
  costUsd: number | null;
  error: string | null;
  createdAt: string;
}

/**
 * Lists a workflow's AI calls (agent_run rows), oldest first.
 *
 * @param workflowId workflow id
 * @returns AgentRunOut[]
 * @throws ApiClientError on non-2xx
 * @sideEffects HTTP GET
 * @idempotent yes
 */
export async function fetchAgentRuns(workflowId: string): Promise<AgentRunOut[]> {
  return api<AgentRunOut[]>(`/api/workflows/${workflowId}/agent-runs`);
}

/** Body for POST /api/workflows/trigger. triggerSource is REQUIRED. */
export interface TriggerWorkflowBody {
  eventId: string;
  type: string;
  triggerSource: string;
  displayName: string;
  initialContext?: Record<string, unknown>;
}

/**
 * Admin-only. Creates a workflow row and dispatches it via the engine.
 * Returns 200 BEFORE step 0 actually runs (engine uses setImmediate).
 * Duplicate (eventId, triggerSource) → 409 INVALID_STATE_TRANSITION.
 *
 * @param body trigger metadata
 * @returns { id, status } of the newly created workflow
 * @throws ApiClientError(403) if not admin; (409) on duplicate triggerSource
 * @sideEffects HTTP POST; engine begins async step execution
 * @idempotent no — re-running with the same triggerSource is rejected by the unique constraint
 */
export async function triggerWorkflow(
  body: TriggerWorkflowBody,
): Promise<{ id: string; status: WorkflowStatus }> {
  return api('/api/workflows/trigger', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Admin-only. Cancels a workflow when it is in {paused, pending, failed}.
 *
 * @param id workflow id
 * @returns { ok: true } on success
 * @throws ApiClientError(403) if not admin; (404) WORKFLOW_NOT_FOUND;
 *         (409) INVALID_STATE_TRANSITION if not cancellable
 * @sideEffects HTTP POST
 * @idempotent yes — second call against an already-cancelled row 409s
 */
export async function cancelWorkflow(id: string): Promise<{ ok: true }> {
  return api(`/api/workflows/${id}/cancel`, { method: 'POST' });
}

/**
 * Admin-only. Hard-deletes a workflow (cascades to StepExecutions,
 * TrackedEmails, AuditEntries). Use for debug/testing churn — frees the
 * (eventId, triggerSource) unique slot so the same dealId can be re-
 * triggered immediately. Production should prefer cancel.
 */
export async function deleteWorkflow(id: string): Promise<{ ok: true }> {
  return api(`/api/workflows/${id}`, { method: 'DELETE' });
}

/**
 * Archives (soft-hides) a workflow. Any authenticated user. Reversible via
 * unarchiveWorkflow — unlike delete, no data is removed; the row just drops
 * out of the default list + stat counts.
 *
 * @param id workflow id
 * @returns { ok: true }
 * @throws ApiClientError(404) WORKFLOW_NOT_FOUND
 * @sideEffects HTTP POST
 * @idempotent yes — archiving an already-archived workflow still 200s
 */
export async function archiveWorkflow(id: string): Promise<{ ok: true }> {
  return api(`/api/workflows/${id}/archive`, { method: 'POST' });
}

/**
 * Restores an archived workflow to the default list. Any authenticated user.
 *
 * @param id workflow id
 * @returns { ok: true }
 * @throws ApiClientError(404) WORKFLOW_NOT_FOUND
 * @sideEffects HTTP POST
 * @idempotent yes
 */
export async function unarchiveWorkflow(id: string): Promise<{ ok: true }> {
  return api(`/api/workflows/${id}/unarchive`, { method: 'POST' });
}

/**
 * Admin-only. Resets a failed step to pending and resumes the workflow.
 * Both workflow.status AND step.status must be 'failed' (engine enforces).
 *
 * @param id workflow id
 * @param stepIndex zero-based step index
 * @returns { ok: true } on success
 * @throws ApiClientError(403) if not admin; (404) WORKFLOW_NOT_FOUND;
 *         (409) INVALID_STATE_TRANSITION if not retryable
 * @sideEffects HTTP POST; engine resumes async step execution
 * @idempotent no — increments retryCount each call
 */
export async function retryStep(id: string, stepIndex: number): Promise<{ ok: true }> {
  return api(`/api/workflows/${id}/retry/${stepIndex}`, { method: 'POST' });
}

/**
 * Restores the Sponsorship Tracker cells a writing step overwrote, from the
 * pre-image snapshot the step captured before its write. Any authenticated
 * user (same policy as gate actions).
 *
 * @param id workflow id
 * @param stepId the writing step ('contract_details_to_tracker' | 'supplementary_review')
 * @returns { ok, rowNumber, revertedColumns }
 * @throws ApiClientError(404) WORKFLOW_NOT_FOUND; (409) NO_TRACKER_PRE_IMAGE
 *         when the step stored no snapshot
 * @sideEffects HTTP POST; writes the pre-image back to the live Sheet
 * @idempotent yes — re-running writes the same pre-image again
 */
export async function revertTrackerChanges(
  id: string,
  stepId: string,
): Promise<{ ok: boolean; rowNumber: number; revertedColumns: string[] }> {
  return api(`/api/workflows/${id}/tracker/revert`, {
    method: 'POST',
    body: JSON.stringify({ stepId }),
  });
}

/**
 * Manually resumes a step in waiting_event status that has no TrackedEmail
 * row backing it (e.g., a receipt step after the email was skipped).
 *
 * @param id workflow id
 * @param stepIndex zero-based step index
 * @param skipped true marks the step skipped; false marks it received
 * @returns { ok: true }
 * @throws ApiClientError(409) if step is not in waiting_event
 * @sideEffects HTTP POST; engine advances the workflow asynchronously
 * @idempotent no
 */
export async function manualResumeStep(
  id: string,
  stepIndex: number,
  skipped: boolean,
): Promise<{ ok: true }> {
  return api(`/api/workflows/${id}/steps/${stepIndex}/manual-resume`, {
    method: 'POST',
    body: JSON.stringify({ skipped }),
  });
}

/**
 * Releases a held band-B step by firing its `holdFor` event on the workflow
 * (e.g. "the prep call happened") — the engine then dispatches the step for
 * real (a gate step opens its gate; it is NOT skipped-through).
 *
 * @param id workflow id
 * @param stepId the DEFINITION step id (e.g. 'prep_notes'), not an index
 * @returns { ok: true, event } — the hold event that was fired
 * @throws ApiClientError(404) WORKFLOW_NOT_FOUND / STEP_NOT_FOUND;
 *         (409) INVALID_STATE_TRANSITION when the step has no holdFor or is
 *         no longer held (already released — stale click)
 * @sideEffects HTTP POST; engine advances the workflow asynchronously
 * @idempotent no — a second call 409s against the now-released step
 */
export async function releaseWorkflowStep(
  id: string,
  stepId: string,
): Promise<{ ok: true; event: string }> {
  return api(`/api/workflows/${id}/release/${stepId}`, { method: 'POST' });
}

/**
 * Redoes a terminal (completed/skipped) rerunnable band-B step — today the
 * card gate ('card_assets'). The server resets the step to pending and the
 * engine reopens its gate in the background; re-approval republishes the
 * deliverables (Drive content-overwrite + fresh monday files). No email.
 *
 * @param id workflow id
 * @param stepId the DEFINITION step id (e.g. 'card_assets'), not an index
 * @returns { ok: true }
 * @throws ApiClientError(404) WORKFLOW_NOT_FOUND;
 *         (409) RERUN_NOT_SUPPORTED for non-rerunnable steps,
 *         INVALID_STATE_TRANSITION when the step/workflow isn't terminal-live
 * @sideEffects HTTP POST; engine reopens the gate asynchronously
 * @idempotent no — a second call 409s while the reopened step is live
 */
export async function rerunWorkflowStep(id: string, stepId: string): Promise<{ ok: true }> {
  return api(`/api/workflows/${id}/rerun/${stepId}`, { method: 'POST' });
}

/**
 * Sets or clears a band-B date-armed step's open date. A datetime moves the
 * step to `scheduled` at that time (past dates fire on the next scheduler
 * tick); null parks it as a needs-date hold the engine will NOT re-fill —
 * it stays held until someone sets a date or triggers it manually.
 *
 * @param id workflow id
 * @param stepIndex zero-based step index
 * @param scheduledFor naive ISO datetime (treated as UTC) or null to clear
 * @returns { ok: true }
 * @throws ApiClientError(404) WORKFLOW_NOT_FOUND / STEP_NOT_FOUND;
 *         (409) INVALID_STATE_TRANSITION when the step isn't date-armed or
 *         is no longer reschedulable (already running/completed)
 * @sideEffects HTTP PATCH; reschedules the step server-side
 * @idempotent yes — re-sending the same date lands the same state
 */
export async function updateStepSchedule(
  id: string,
  stepIndex: number,
  scheduledFor: string | null,
): Promise<{ ok: true }> {
  return api(`/api/workflows/${id}/steps/${stepIndex}/schedule`, {
    method: 'PATCH',
    body: JSON.stringify({ scheduledFor }),
  });
}

/**
 * Fetches the welcome→ops Gmail thread for the ops_next_steps gate viewer.
 * Reads from the reviewer's resolved sending mailbox; pass the gate's selected
 * "send from" connection so the thread comes from the account they'll send as.
 * Never throws for the unavailable cases — the server returns
 * `{ available: false, reason }` (only a missing workflow 404s).
 *
 * @param id workflow id
 * @param sendFromConnectionId optional GmailConnection id (gate dropdown)
 * @returns OpsEmailThread
 * @sideEffects HTTP GET (live Gmail read server-side)
 * @idempotent yes
 */
export async function fetchOpsEmailThread(
  id: string,
  sendFromConnectionId?: string,
): Promise<OpsEmailThread> {
  const params = new URLSearchParams();
  if (sendFromConnectionId) params.set('send_from_connection_id', sendFromConnectionId);
  const qs = params.toString();
  return api<OpsEmailThread>(`/api/workflows/${id}/ops-email-thread${qs ? `?${qs}` : ''}`);
}
